import type { MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { isManager } from 'modules/employees/lib/isManager'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import { launchSurveys as canLaunchSurveys } from 'modules/engagement/lib/authorization'
import { enpsComingSoonImage } from 'modules/engagement_insights/assets/Images/index'
import { useEngagementInsightsData } from 'modules/engagement_insights/hooks/useEngagementInsightsData'
import { SelectedSurvey } from 'modules/engagement_insights/lib/widget'

type Role = 'admin' | 'manager'

const getLaunchTitle = (userRole: Role) => {
  if (userRole === 'manager') {
    return i18n.t(`engagement_insights.enps_widget.launch.manager.title`)
  }

  return i18n.t('engagement_insights.enps_widget.launch.admin.title')
}

const getDescriptionByRole = (userRole: Role) => {
  if (userRole === 'manager') {
    return i18n.t(`engagement_insights.enps_widget.launch.manager.description`)
  }

  return i18n.t(`engagement_insights.enps_widget.launch.admin.description`)
}

const getDescription1ByRole = (userRole: Role) => {
  if (userRole === 'manager') {
    return i18n.t(`engagement_insights.enps_widget.launch.manager.description_1`)
  }

  return i18n.t(`engagement_insights.enps_widget.launch.admin.description_1`)
}

const EnpsWidgetLaunch = () => {
  const { employee: currentEmployee } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { moduleAvailabilities, canManageSurveys } = useEngagementContext()
  const canLaunchSurveysValue = canLaunchSurveys(moduleAvailabilities, canManageSurveys)
  const { enpsStatus, climateStatus, setSurveyToBeLaunched } = useEngagementInsightsData()
  const userRole = isManager(currentEmployee) ? 'manager' : 'admin'

  const title = getLaunchTitle(userRole)

  const HELP_CENTER_URL = 'https://help.factorialhr.com/all-about-enps'

  const launchSurveys = () => {
    setSurveyToBeLaunched(SelectedSurvey.Enps)
    trackAmplitudeEvents(trackingEvents.engagementInsights.startEngagementSurveys)
    navigate(navigation.engagement.insights.launch.fullPath())
  }

  const orangeElipse = (
    <Box
      background="champagne800"
      style={{
        width: '70%',
        height: '70%',
        position: 'absolute',
        borderRadius: '50%',
        bottom: '30%',
        left: '50%',
        filter: 'blur(97px)',
        opacity: '0.2',
      }}
    />
  )

  const purpleElipse = (
    <Box
      background="radicalRed800"
      style={{
        width: '470px',
        height: '100%',
        position: 'absolute',
        borderRadius: '50%',
        bottom: '70%',
        left: '-45%',
        filter: 'blur(65px)',
        opacity: '0.2',
      }}
    />
  )

  const trackAmplitudeEvents = (eventName: string) => {
    track(eventName, {
      enps_status: enpsStatus,
      climate_status: climateStatus,
    })
  }

  const goToHC = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    trackAmplitudeEvents(trackingEvents.engagementInsights.goToHC)
    window.open(HELP_CENTER_URL, '_blank')
  }

  return (
    <Box
      borderRadius={{
        all: 'abs008',
      }}
      style={{
        position: 'relative',
        overflow: 'hidden',
        flex: '1 1 0',
        border: '1px solid transparent',
      }}
      background="grey100"
      alignItems="center"
      padding="s24"
    >
      {orangeElipse}
      {purpleElipse}
      <Box>
        <img src={enpsComingSoonImage} alt="enps coming soon" style={{ zIndex: 1 }} />
      </Box>
      <Box gap="s8" paddingTop="s24">
        <Box>
          <Text weight="semibold" color="grey1000" size="400">
            {title}
          </Text>
        </Box>
        <Box style={{ fontSize: '16px', lineHeight: '24px' }}>
          <Text weight="normal" color="grey800" size="300">
            {/* TODO: Replace all this code by a better sentence and use i18n.tView function */}
            {getDescriptionByRole(userRole)}
            &nbsp;
            <a
              style={{ outline: 0 }}
              href={HELP_CENTER_URL}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => goToHC(event)}
            >
              {i18n.t(`engagement_insights.enps_widget.launch.enps`) + ' (eNPS)'}
            </a>
            &nbsp;{i18n.t(`engagement_insights.enps_widget.launch.engagement_survey`)}
            {userRole === 'manager' && (
              <span>
                &nbsp;
                {getDescription1ByRole(userRole)}
              </span>
            )}
          </Text>
        </Box>
        {canLaunchSurveysValue && (
          <Box paddingTop="s16">
            <Button onClick={() => launchSurveys()}>
              {i18n.t('engagement_insights.check_and_launch')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default observer(EnpsWidgetLaunch)
